import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useUserRole } from "@/hooks/useUserRole";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { Loader2, Calendar as CalendarIcon } from "lucide-react";
import {
  localInputToUtcIso,
  utcIsoToLocalInput,
  buildRecurringOccurrences,
} from "@/lib/dateTime";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface CalendarEvent {
  id?: string;
  student_user_id: string;
  creator_id: string;
  creator_type: "student" | "parent" | "tutor";
  title: string;
  description?: string;
  start_time: string;
  end_time: string;
  color?: string;
  series_id?: string | null;
}

type EditScope = "this" | "future" | "all";

interface CalendarEventDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  event?: CalendarEvent | null;
  studentUserId?: string;
  defaultDate?: Date;
  onEventSaved: () => void;
}

const WEEK_DAYS = [
  { value: 0, label: "Sun" },
  { value: 1, label: "Mon" },
  { value: 2, label: "Tue" },
  { value: 3, label: "Wed" },
  { value: 4, label: "Thu" },
  { value: 5, label: "Fri" },
  { value: 6, label: "Sat" },
];

export default function CalendarEventDialog({
  open,
  onOpenChange,
  event,
  studentUserId,
  defaultDate,
  onEventSaved,
}: CalendarEventDialogProps) {
  const { user } = useAuth();
  const { role } = useUserRole();
  const [loading, setLoading] = useState(false);
  const [scopeDialogOpen, setScopeDialogOpen] = useState(false);
  const [pendingScope, setPendingScope] = useState<EditScope | null>(null);

  // True when editing an existing event that belongs to a recurring series.
  const isRecurringEdit = Boolean(event?.id && event?.series_id);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    // Stored as `datetime-local` string in user's local TZ (e.g. "2025-04-25T09:00")
    start_time: "",
    end_time: "",
    color: "#3b82f6",
    is_recurring: false,
    recurring_type: "daily" as "daily" | "custom",
    recurring_days: [] as number[], // weekdays to INCLUDE
    excluded_days: [] as number[], // weekdays to EXCLUDE (daily mode only)
    recurring_end_date: "",
  });

  // Reset/populate when dialog opens or the event changes
  useEffect(() => {
    if (!open) return;
    if (event) {
      setFormData((prev) => ({
        ...prev,
        title: event.title,
        description: event.description || "",
        start_time: utcIsoToLocalInput(event.start_time),
        end_time: utcIsoToLocalInput(event.end_time),
        color: event.color || "#3b82f6",
        is_recurring: false,
        recurring_type: "daily",
        recurring_days: [],
        excluded_days: [],
        recurring_end_date: "",
      }));
    } else if (defaultDate) {
      const base = new Date(defaultDate);
      base.setMinutes(0, 0, 0);
      const start = new Date(base);
      const end = new Date(base);
      end.setHours(end.getHours() + 1);
      setFormData((prev) => ({
        ...prev,
        title: "",
        description: "",
        start_time: utcIsoToLocalInput(start.toISOString()),
        end_time: utcIsoToLocalInput(end.toISOString()),
        color: "#3b82f6",
        is_recurring: false,
        recurring_type: "daily",
        recurring_days: [],
        excluded_days: [],
        recurring_end_date: "",
      }));
    }
  }, [open, event, defaultDate]);

  const getCreatorType = (): "student" | "parent" | "tutor" => {
    if (role === "parent") return "parent";
    if (role === "instructor") return "tutor";
    return "student";
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user || !studentUserId) {
      toast.error("Missing user information");
      return;
    }
    if (!formData.title.trim()) {
      toast.error("Title is required");
      return;
    }
    if (!formData.start_time || !formData.end_time) {
      toast.error("Start and end times are required");
      return;
    }
    const startUtc = localInputToUtcIso(formData.start_time);
    const endUtc = localInputToUtcIso(formData.end_time);
    if (new Date(endUtc) <= new Date(startUtc)) {
      toast.error("End time must be after start time");
      return;
    }
    if (formData.is_recurring && !formData.recurring_end_date) {
      toast.error("Please select an end date for recurring events");
      return;
    }
    if (
      formData.is_recurring &&
      formData.recurring_type === "custom" &&
      formData.recurring_days.length === 0
    ) {
      toast.error("Please select at least one day for custom recurring events");
      return;
    }

    // If editing an event that belongs to a recurring series, ask the user
    // whether the change should apply to just this occurrence, this & future,
    // or the entire series. The actual save runs in `applyEdit` after the
    // scope is chosen.
    if (isRecurringEdit && pendingScope === null) {
      setScopeDialogOpen(true);
      return;
    }

    setLoading(true);
    try {
      if (formData.is_recurring && !event?.id) {
        await createRecurringEvents(startUtc, endUtc);
      } else {
        await createSingleEvent(startUtc, endUtc, pendingScope ?? "this");
      }
      onEventSaved();
      onOpenChange(false);
    } catch (error: any) {
      console.error("Error saving event:", error);
      toast.error(error.message || "Failed to save event");
    } finally {
      setLoading(false);
      setPendingScope(null);
    }
  };

  // Run after the user picks a scope in the confirmation dialog.
  const confirmScopeAndSave = async (scope: EditScope) => {
    setScopeDialogOpen(false);
    setPendingScope(scope);
    // Re-run save with chosen scope on next tick so state is committed.
    setTimeout(() => {
      const form = document.getElementById(
        "calendar-event-form"
      ) as HTMLFormElement | null;
      form?.requestSubmit();
    }, 0);
  };

  const createSingleEvent = async (
    startUtc: string,
    endUtc: string,
    scope: EditScope = "this"
  ) => {
    if (!user || !studentUserId) return;
    // Fields that are safe to propagate across a series (content/appearance).
    const sharedFields = {
      title: formData.title.trim(),
      description: formData.description?.trim() || null,
      color: formData.color,
    };
    // Per-occurrence fields (times) — only applied to the current event.
    const eventData = {
      student_user_id: studentUserId,
      creator_id: user.id,
      creator_type: getCreatorType(),
      ...sharedFields,
      start_time: startUtc,
      end_time: endUtc,
    };

    if (event?.id) {
      // 1. Always update the current occurrence with full data (incl. times).
      const { error } = await supabase
        .from("calendar_events")
        .update(eventData)
        .eq("id", event.id);
      if (error) throw error;

      // 2. Optionally propagate shared fields to other occurrences.
      if (scope !== "this" && event.series_id) {
        let q = supabase
          .from("calendar_events")
          .update(sharedFields)
          .eq("series_id", event.series_id)
          .is("deleted_at", null)
          .neq("id", event.id);

        if (scope === "future") {
          q = q.gte("start_time", event.start_time);
        }
        const { error: bulkError } = await q;
        if (bulkError) throw bulkError;
      }

      toast.success(
        scope === "all"
          ? "All events in series updated"
          : scope === "future"
          ? "This and future events updated"
          : "Event updated"
      );
    } else {
      const { error } = await supabase.from("calendar_events").insert([eventData]);
      if (error) throw error;
      toast.success("Event created");
    }
  };

  const createRecurringEvents = async (startUtc: string, endUtc: string) => {
    if (!user || !studentUserId) return;

    const occurrences = buildRecurringOccurrences({
      startLocalInput: formData.start_time,
      endLocalInput: formData.end_time,
      untilLocalDate: formData.recurring_end_date,
      mode: formData.recurring_type,
      includeDays: formData.recurring_days,
      excludeDays: formData.excluded_days,
    });

    if (occurrences.length === 0) {
      toast.error("No events to create with the selected pattern");
      return;
    }
    if (occurrences.length > 365) {
      toast.error("Too many events. Reduce the date range or change the pattern.");
      return;
    }

    // Generate one series_id shared by every occurrence so we can later
    // edit/delete the entire series (or "this & future") reliably.
    const seriesId =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

    const events = occurrences.map((occ) => ({
      student_user_id: studentUserId,
      creator_id: user.id,
      creator_type: getCreatorType(),
      title: formData.title.trim(),
      description: formData.description?.trim() || null,
      start_time: occ.startUtc,
      end_time: occ.endUtc,
      color: formData.color,
      series_id: seriesId,
    }));

    const { error } = await supabase.from("calendar_events").insert(events);
    if (error) throw error;
    toast.success(`Created ${events.length} recurring events`);
    // Mark startUtc/endUtc as intentionally unused — first occurrence is in `events`.
    void startUtc;
    void endUtc;
  };

  const toggleDay = (key: "recurring_days" | "excluded_days", day: number) => {
    setFormData((prev) => {
      const list = prev[key];
      return {
        ...prev,
        [key]: list.includes(day)
          ? list.filter((d) => d !== day)
          : [...list, day].sort(),
      };
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{event ? "Edit Event" : "Add Event"}</DialogTitle>
          <DialogDescription>
            {event ? "Update the event details below." : "Create a new calendar event."}
          </DialogDescription>
        </DialogHeader>

        <form id="calendar-event-form" onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">Title *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Event title"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              placeholder="Event description (optional)"
              rows={3}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="start_time">Start Time *</Label>
              <Input
                id="start_time"
                type="datetime-local"
                value={formData.start_time}
                onChange={(e) =>
                  setFormData({ ...formData, start_time: e.target.value })
                }
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="end_time">End Time *</Label>
              <Input
                id="end_time"
                type="datetime-local"
                value={formData.end_time}
                onChange={(e) =>
                  setFormData({ ...formData, end_time: e.target.value })
                }
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="color">Color</Label>
            <div className="flex gap-2">
              <Input
                id="color"
                type="color"
                value={formData.color}
                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                className="w-20 h-10"
              />
              <Input
                type="text"
                value={formData.color}
                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                placeholder="#3b82f6"
                className="flex-1"
              />
            </div>
          </div>

          {!event?.id && (
            <div className="space-y-4 pt-4 border-t">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="is_recurring"
                  checked={formData.is_recurring}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, is_recurring: checked as boolean })
                  }
                />
                <Label htmlFor="is_recurring" className="cursor-pointer">
                  <div className="flex items-center gap-2">
                    <CalendarIcon className="h-4 w-4" />
                    <span>Make this a recurring event</span>
                  </div>
                </Label>
              </div>

              {formData.is_recurring && (
                <div className="space-y-4 pl-6 border-l-2">
                  <RadioGroup
                    value={formData.recurring_type}
                    onValueChange={(value: "daily" | "custom") =>
                      setFormData({ ...formData, recurring_type: value })
                    }
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="daily" id="daily" />
                      <Label htmlFor="daily" className="cursor-pointer">
                        Every day
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="custom" id="custom" />
                      <Label htmlFor="custom" className="cursor-pointer">
                        Custom days
                      </Label>
                    </div>
                  </RadioGroup>

                  {formData.recurring_type === "custom" && (
                    <div className="space-y-2">
                      <Label>Include Days</Label>
                      <div className="grid grid-cols-7 gap-2">
                        {WEEK_DAYS.map((day) => (
                          <Button
                            key={day.value}
                            type="button"
                            variant={
                              formData.recurring_days.includes(day.value)
                                ? "default"
                                : "outline"
                            }
                            size="sm"
                            className="h-10"
                            onClick={() => toggleDay("recurring_days", day.value)}
                          >
                            {day.label}
                          </Button>
                        ))}
                      </div>
                    </div>
                  )}

                  {formData.recurring_type === "daily" && (
                    <div className="space-y-2">
                      <Label>Exclude Days (optional)</Label>
                      <div className="grid grid-cols-7 gap-2">
                        {WEEK_DAYS.map((day) => (
                          <Button
                            key={day.value}
                            type="button"
                            variant={
                              formData.excluded_days.includes(day.value)
                                ? "destructive"
                                : "outline"
                            }
                            size="sm"
                            className="h-10"
                            onClick={() => toggleDay("excluded_days", day.value)}
                          >
                            {day.label}
                          </Button>
                        ))}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        E.g. exclude Sat/Sun for weekdays-only.
                      </p>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="recurring_end_date">Repeat Until *</Label>
                    <Input
                      id="recurring_end_date"
                      type="date"
                      value={formData.recurring_end_date}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          recurring_end_date: e.target.value,
                        })
                      }
                      min={
                        formData.start_time
                          ? formData.start_time.slice(0, 10)
                          : undefined
                      }
                      required={formData.is_recurring}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {event ? "Update Event" : "Create Event"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>

      <AlertDialog
        open={scopeDialogOpen}
        onOpenChange={(o) => {
          setScopeDialogOpen(o);
          if (!o) setPendingScope(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Edit Recurring Event</AlertDialogTitle>
            <AlertDialogDescription>
              This event is part of a recurring series. How should your changes
              be applied? Time changes only affect the current event; title,
              description, and color can propagate.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <Button variant="outline" onClick={() => confirmScopeAndSave("this")}>
              This Event Only
            </Button>
            <Button variant="outline" onClick={() => confirmScopeAndSave("future")}>
              This & Future Events
            </Button>
            <AlertDialogAction onClick={() => confirmScopeAndSave("all")}>
              All Events in Series
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
